'use client'

import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import 'dayjs/locale/en'

import { useGetTripListQuery } from '@/src/api/rtkQuery/tripListApi'
import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { TripSummary } from '@/src/types/trips'
import { formatTripDateParts, formatTripTitleParts } from '@/src/utils/trips/tripListHelpers'
import { UpcomingTripNudge, UpcomingTripsDrawer } from './UpcomingTripNudge'

const UPCOMING_TRIP_NUDGE_SESSION_KEY = 'showUpcomingTrips'
const FLIGHT_TRIP_TYPES = new Set(['air', 'flight'])
const HOTEL_TRIP_TYPES = new Set(['hotel'])

const isAirTrip = (tripType?: string) => FLIGHT_TRIP_TYPES.has((tripType || '').toLowerCase().trim())

const getTripDetailsPath = ({
    tripId,
    tripType,
    bookingSource,
    locale
}: {
    tripId?: string
    tripType?: string
    bookingSource?: 'MAIN' | 'LINKED'
    locale: string
}) => {
    if (!tripId) return ''
    const query = bookingSource === 'LINKED' ? '?account=LINKED' : ''
    if (isAirTrip(tripType)) {
        return `/${locale}/trips/${tripId}${query}`
    }
    return `/accounts/trips/${tripId}${query}`
}

const getTripIconType = (tripType?: string): 'flight' | 'hotel' => {
    const normalizedType = (tripType || '').toLowerCase().trim()
    if (HOTEL_TRIP_TYPES.has(normalizedType)) return 'hotel'
    if (FLIGHT_TRIP_TYPES.has(normalizedType)) return 'flight'
    return 'flight'
}

const UpcomingTripNudgeWrapper: React.FC = () => {
    const locale = useLocale()
    const tCommon = useTranslations('common')
    const tTrips = useTranslations('tripList')
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn
    const [isDismissedInSession, setIsDismissedInSession] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    const { data } = useGetTripListQuery(
        {
            pageNo: 0,
            pageSize: 10,
            tripStatus: 'upcoming'
        },
        { skip: !isLoggedIn }
    )

    useEffect(() => {
        if (typeof window === 'undefined') return
        const wasDismissed = window.sessionStorage.getItem(UPCOMING_TRIP_NUDGE_SESSION_KEY) === 'true'
        setIsDismissedInSession(wasDismissed)
    }, [])

    const sortedUpcomingTrips = useMemo<TripSummary[]>(() => {
        const trips = data?.upcomingTrips || []
        if (!trips.length) return []

        return [...trips].sort((a, b) => {
            const dateA = a.travelDate ? dayjs(a.travelDate.replace('+0000', 'Z')).valueOf() : Number.MAX_SAFE_INTEGER
            const dateB = b.travelDate ? dayjs(b.travelDate.replace('+0000', 'Z')).valueOf() : Number.MAX_SAFE_INTEGER
            return dateA - dateB
        })
    }, [data?.upcomingTrips])

    const upcomingTrip = useMemo<TripSummary | null>(() => {
        return sortedUpcomingTrips[0] || null
    }, [sortedUpcomingTrips])

    const tripLabel = useMemo(() => {
        if (!upcomingTrip) return ''

        if (!upcomingTrip.travelDate) return ''

        const parsedDate = dayjs(upcomingTrip.travelDate.replace('+0000', 'Z'))
        return parsedDate.isValid()
            ? parsedDate.locale(locale).format('D MMM')
            : tCommon('upcomingTripNudge.dateUnknown')
    }, [locale, tCommon, upcomingTrip])

    const tripTitleParts = useMemo(() => {
        if (!upcomingTrip) return null
        return formatTripTitleParts(upcomingTrip, tTrips('untitledTrip', { tripId: upcomingTrip.tripId }))
    }, [tTrips, upcomingTrip])

    const nudgeTripIconType = useMemo<'flight' | 'hotel'>(() => {
        if (!upcomingTrip) return 'flight'
        return getTripIconType(upcomingTrip.tripType)
    }, [upcomingTrip])

    const drawerRows = useMemo(() => {
        return sortedUpcomingTrips.map(trip => {
            const dateParts = formatTripDateParts(trip.travelDate, locale, tCommon('upcomingTripNudge.dateUnknown'))
            const titleParts = formatTripTitleParts(trip, tTrips('untitledTrip', { tripId: trip.tripId }))
            return {
                id: trip.tripId,
                titleParts,
                dateLabel: dateParts.dateLabel || tCommon('upcomingTripNudge.dateUnknown'),
                iconType: getTripIconType(trip.tripType),
                tripType: trip.tripType,
                bookingSource: trip.bookingSource
            }
        })
    }, [locale, sortedUpcomingTrips, tCommon, tTrips])

    const handleDismiss = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(UPCOMING_TRIP_NUDGE_SESSION_KEY, 'true')
        }
        setIsDismissedInSession(true)
    }, [])

    const handleNavigate = useCallback(() => {
        if (!upcomingTrip?.tripId || typeof window === 'undefined') return
        const destination = getTripDetailsPath({
            tripId: upcomingTrip.tripId,
            tripType: upcomingTrip.tripType,
            bookingSource: upcomingTrip.bookingSource,
            locale
        })
        if (destination) {
            window.location.href = destination
        }
    }, [locale, upcomingTrip])

    const handleCheckAllOpen = useCallback(() => {
        setIsDrawerOpen(true)
    }, [])

    const handleDrawerClose = useCallback(() => {
        setIsDrawerOpen(false)
    }, [])

    const handleViewAll = useCallback(() => {
        if (typeof window === 'undefined') return
        window.location.href = `/${locale}/trips`
    }, [locale])

    const handleTripCardClick = useCallback(
        (tripId: string, tripType?: string, bookingSource?: 'MAIN' | 'LINKED') => {
            if (!tripId || typeof window === 'undefined') return
            const destination = getTripDetailsPath({
                tripId,
                tripType,
                bookingSource,
                locale
            })
            if (destination) {
                window.location.href = destination
            }
        },
        [locale]
    )

    if (!isLoggedIn || isDismissedInSession || !upcomingTrip || !tripTitleParts) return null

    return (
        <>
            <UpcomingTripNudge
                title={tCommon('upcomingTripNudge.title')}
                tripTitleParts={tripTitleParts}
                tripDateLabel={tripLabel}
                tripIconType={nudgeTripIconType}
                showCheckAll={sortedUpcomingTrips.length > 1}
                checkAllLabel={tCommon('upcomingTripNudge.checkAll')}
                onCheckAllClick={handleCheckAllOpen}
                onNavigate={handleNavigate}
                onDismiss={handleDismiss}
            />
            <UpcomingTripsDrawer
                open={isDrawerOpen}
                title={tCommon('upcomingTripNudge.upcomingTrips')}
                viewAllLabel={tCommon('upcomingTripNudge.viewAll')}
                rows={drawerRows}
                onClose={handleDrawerClose}
                onViewAll={handleViewAll}
                onRowClick={handleTripCardClick}
            />
        </>
    )
}

export { UpcomingTripNudgeWrapper }
export default UpcomingTripNudgeWrapper
